import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import API_ENDPOINTS from "../config/api";

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const TilesSection = styled.section`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2rem;
  margin-top: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const FiltersBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: #013566;
`;

const StatusFilterButton = styled.button<{ $active?: boolean }>`
  padding: 0.65rem 1.25rem;
  border: 1px solid #013566;
  border-radius: 8px;
  background-color: ${(props) => (props.$active ? "#013566" : "white")};
  color: ${(props) => (props.$active ? "white" : "#013566")};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) => (props.$active ? "#012a4a" : "#eef3f8")};
    transform: translateY(-1px);
  }
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;
  min-width: 220px;
  flex: 1;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const CreateButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background-color: #013566;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: auto;

  &:hover {
    background-color: #012a4a;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Tile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
`;

const TileHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #eee;

  h2 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #013566;
    margin: 0;
    flex: 1;
  }
`;

const TileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TileRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
  gap: 1rem;
  padding: 12px 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 12px 8px;
    margin: 0 -8px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TileElement = styled.div`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0;
  flex: 1;
  display: flex;
  align-items: center;
  word-break: break-all;
`;

const UrlLink = styled.a`
  color: #013566;
  text-decoration: underline;

  &:hover {
    color: #012a4a;
  }
`;

const StatusBadge = styled.span<{ $isActive?: boolean }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
  background-color: ${(props) => (props.$isActive ? "#4caf50" : "#ffc107")};
  color: white;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eee;
`;

const PaginationButton = styled.button<{ $disabled?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid #013566;
  border-radius: 6px;
  background: ${(props) => (props.$disabled ? "#f8f9fa" : "#013566")};
  color: ${(props) => (props.$disabled ? "#6c757d" : "white")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #012a4a;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const PageInfo = styled.span`
  font-weight: 500;
  color: #013566;
  font-size: 0.9rem;
`;

const EditButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #013566;
  border-radius: 6px;
  background-color: transparent;
  color: #013566;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #013566;
    color: white;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContent = styled.div`
  position: relative;
  width: 500px;
  max-width: 90%;
  border-radius: 12px;
  background-color: white;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #013566;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #013566;
  }
`;

const ModalFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ModalLabel = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #013566;
`;

const ModalInput = styled.input<{ $hasError?: boolean }>`
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => (props.$hasError ? "#d32f2f" : "#ddd")};
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${(props) => (props.$hasError ? "#d32f2f" : "#013566")};
    box-shadow: 0 0 0 2px
      ${(props) =>
        props.$hasError ? "rgba(211, 47, 47, 0.2)" : "rgba(1, 53, 102, 0.2)"};
  }
`;

const FieldError = styled.span`
  font-size: 0.85rem;
  color: #d32f2f;
`;

const ModalSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

const ModalButton = styled.button<{ $variant?: "secondary" }>`
  padding: 0.75rem 1.5rem;
  border: ${(props) =>
    props.$variant === "secondary" ? "1px solid #013566" : "none"};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.$variant === "secondary" ? "transparent" : "#013566"};
  color: ${(props) =>
    props.$variant === "secondary" ? "#013566" : "white"};

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.$variant === "secondary" ? "#eef3f8" : "#012a4a"};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

type Partner = {
  id: number;
  name: string;
  status: number;
  privacy_policy_url?: string | null;
  terms_and_conditions_url?: string | null;
  created_at: string;
  updated_at: string;
};

const renderUrlCell = (url?: string | null) => {
  if (!url) {
    return "-";
  }

  return (
    <UrlLink href={url} target="_blank" rel="noopener noreferrer">
      {url}
    </UrlLink>
  );
};

const itemsPerPage = 10;

type PartnerUrlErrors = {
  privacy_policy_url?: string;
  terms_and_conditions_url?: string;
};

const isValidHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const getPartnerUrlErrors = (
  privacyUrl: string,
  termsUrl: string,
): PartnerUrlErrors => {
  const errors: PartnerUrlErrors = {};
  const trimmedPrivacyUrl = privacyUrl.trim();
  const trimmedTermsUrl = termsUrl.trim();

  if (trimmedPrivacyUrl && !isValidHttpUrl(trimmedPrivacyUrl)) {
    errors.privacy_policy_url = "The privacy policy url field must be a valid URL.";
  }

  if (trimmedTermsUrl && !isValidHttpUrl(trimmedTermsUrl)) {
    errors.terms_and_conditions_url =
      "The terms and conditions url field must be a valid URL.";
  }

  return errors;
};

const Partners = () => {
  const token = localStorage.getItem("token");
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [editName, setEditName] = useState("");
  const [editStatus, setEditStatus] = useState(1);
  const [editPrivacyPolicyUrl, setEditPrivacyPolicyUrl] = useState("");
  const [editTermsAndConditionsUrl, setEditTermsAndConditionsUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState<0 | 1>(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createStatus, setCreateStatus] = useState(1);
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState("");
  const [termsAndConditionsUrl, setTermsAndConditionsUrl] = useState("");
  const [creating, setCreating] = useState(false);
  const [createErrors, setCreateErrors] = useState<PartnerUrlErrors>({});
  const [editErrors, setEditErrors] = useState<PartnerUrlErrors>({});

  const fetchPartners = useCallback(
    async (page: number, status: 0 | 1, search: string) => {
      if (!token) return;

      try {
        setLoading(true);
        const response = await axios.get(API_ENDPOINTS.PARTNERS, {
          params: {
            page,
            status,
            per_page: itemsPerPage,
            ...(search ? { search } : {}),
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log(response.data);

        const perPage = response.data?.per_page ?? itemsPerPage;
        const total = response.data?.total ?? 0;
        const pageCount =
          response.data?.last_page ?? Math.max(1, Math.ceil(total / perPage));

        setPartners(response.data?.data || []);
        setCurrentPage(response.data?.current_page ?? page);
        setTotalPages(pageCount);
      } catch (error) {
        console.error("Error fetching partners:", error);
        setPartners([]);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  useEffect(() => {
    fetchPartners(currentPage, statusFilter, debouncedSearchQuery.trim());
  }, [currentPage, statusFilter, debouncedSearchQuery, fetchPartners]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery]);

  const handleStatusFilterChange = (status: 0 | 1) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const openEditModal = (partner: Partner) => {
    setEditingPartner(partner);
    setEditName(partner.name);
    setEditStatus(partner.status);
    setEditPrivacyPolicyUrl(partner.privacy_policy_url || "");
    setEditTermsAndConditionsUrl(partner.terms_and_conditions_url || "");
    setEditErrors({});
  };

  const closeEditModal = () => {
    setEditingPartner(null);
    setEditName("");
    setEditStatus(1);
    setEditPrivacyPolicyUrl("");
    setEditTermsAndConditionsUrl("");
    setEditErrors({});
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
    setCreateName("");
    setCreateStatus(1);
    setPrivacyPolicyUrl("");
    setTermsAndConditionsUrl("");
    setCreateErrors({});
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateName("");
    setCreateStatus(1);
    setPrivacyPolicyUrl("");
    setTermsAndConditionsUrl("");
    setCreateErrors({});
  };

  const handleCreatePartner = async () => {
    if (!token || !createName.trim()) return;

    const validationErrors = getPartnerUrlErrors(
      privacyPolicyUrl,
      termsAndConditionsUrl,
    );

    if (Object.keys(validationErrors).length > 0) {
      setCreateErrors(validationErrors);
      return;
    }

    setCreateErrors({});

    const payload: {
      name: string;
      status: number;
      privacy_policy_url?: string;
      terms_and_conditions_url?: string;
    } = {
      name: createName.trim(),
      status: createStatus,
    };

    const trimmedPrivacyUrl = privacyPolicyUrl.trim();
    const trimmedTermsUrl = termsAndConditionsUrl.trim();

    if (trimmedPrivacyUrl) {
      payload.privacy_policy_url = trimmedPrivacyUrl;
    }

    if (trimmedTermsUrl) {
      payload.terms_and_conditions_url = trimmedTermsUrl;
    }

    try {
      setCreating(true);
      await axios.post(API_ENDPOINTS.PARTNERS, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      closeCreateModal();
      setCurrentPage(1);
      fetchPartners(1, statusFilter, debouncedSearchQuery.trim());
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        setCreateErrors({
          privacy_policy_url: apiErrors.privacy_policy_url?.[0],
          terms_and_conditions_url: apiErrors.terms_and_conditions_url?.[0],
        });
      }
      console.error("Error creating partner:", error);
    } finally {
      setCreating(false);
    }
  };

  const handleSavePartner = async () => {
    if (!token || !editingPartner) return;

    const validationErrors = getPartnerUrlErrors(
      editPrivacyPolicyUrl,
      editTermsAndConditionsUrl,
    );

    if (Object.keys(validationErrors).length > 0) {
      setEditErrors(validationErrors);
      return;
    }

    setEditErrors({});

    const payload: {
      name: string;
      status: number;
      privacy_policy_url?: string;
      terms_and_conditions_url?: string;
    } = {
      name: editName.trim(),
      status: editStatus,
    };

    const trimmedPrivacyUrl = editPrivacyPolicyUrl.trim();
    const trimmedTermsUrl = editTermsAndConditionsUrl.trim();

    if (trimmedPrivacyUrl) {
      payload.privacy_policy_url = trimmedPrivacyUrl;
    }

    if (trimmedTermsUrl) {
      payload.terms_and_conditions_url = trimmedTermsUrl;
    }

    try {
      setSaving(true);
      await axios.patch(
        API_ENDPOINTS.PARTNER_DETAILS(editingPartner.id),
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      closeEditModal();
      fetchPartners(currentPage, statusFilter, debouncedSearchQuery.trim());
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        setEditErrors({
          privacy_policy_url: apiErrors.privacy_policy_url?.[0],
          terms_and_conditions_url: apiErrors.terms_and_conditions_url?.[0],
        });
      }
      console.error("Error updating partner:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <PageTitle>Partners</PageTitle>

      <FiltersBar>
        <FilterLabel>Status:</FilterLabel>
        <StatusFilterButton
          type="button"
          $active={statusFilter === 1}
          onClick={() => handleStatusFilterChange(1)}
        >
          Active
        </StatusFilterButton>
        <StatusFilterButton
          type="button"
          $active={statusFilter === 0}
          onClick={() => handleStatusFilterChange(0)}
        >
          Inactive
        </StatusFilterButton>
        <SearchInput
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by partner name..."
        />
        <CreateButton type="button" onClick={openCreateModal}>
          Create Partner
        </CreateButton>
      </FiltersBar>

      <TilesSection>
        <Tile>
          <TileHeader>
            <h2>ID</h2>
            <h2>Name</h2>
            <h2>Status</h2>
            <h2>Privacy</h2>
            <h2>Terms</h2>
            <h2>Edit</h2>
          </TileHeader>

          <TileSection>
            {loading ? (
              <TileRow>
                <TileElement style={{ width: "100%", textAlign: "center" }}>
                  Loading partners...
                </TileElement>
              </TileRow>
            ) : partners.length > 0 ? (
              partners.map((partner) => (
                <TileRow key={partner.id}>
                  <TileElement>{partner.id}</TileElement>
                  <TileElement>{partner.name || "N/A"}</TileElement>
                  <TileElement>
                    <StatusBadge $isActive={partner.status === 1}>
                      {partner.status === 1 ? "Active" : "Inactive"}
                    </StatusBadge>
                  </TileElement>
                  <TileElement>
                    {renderUrlCell(partner.privacy_policy_url)}
                  </TileElement>
                  <TileElement>
                    {renderUrlCell(partner.terms_and_conditions_url)}
                  </TileElement>
                  <TileElement>
                    <EditButton onClick={() => openEditModal(partner)}>
                      Edit
                    </EditButton>
                  </TileElement>
                </TileRow>
              ))
            ) : (
              <TileRow>
                <TileElement style={{ width: "100%", textAlign: "center" }}>
                  No partners found
                </TileElement>
              </TileRow>
            )}
          </TileSection>

          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationButton
                $disabled={currentPage <= 1}
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                ← Previous
              </PaginationButton>
              <PageInfo>
                Page {currentPage} of {totalPages}
              </PageInfo>
              <PaginationButton
                $disabled={currentPage >= totalPages}
                disabled={currentPage >= totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                Next →
              </PaginationButton>
            </PaginationContainer>
          )}
        </Tile>
      </TilesSection>

      {showCreateModal && (
        <ModalOverlay onClick={closeCreateModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Create Partner</h2>
              <CloseButton
                type="button"
                onClick={closeCreateModal}
                aria-label="Close"
              >
                ×
              </CloseButton>
            </ModalHeader>

            <ModalFormGroup>
              <ModalLabel htmlFor="create-partner-name">Name</ModalLabel>
              <ModalInput
                id="create-partner-name"
                type="text"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Partner name"
              />
            </ModalFormGroup>

            <ModalFormGroup>
              <ModalLabel htmlFor="create-partner-status">Status</ModalLabel>
              <ModalSelect
                id="create-partner-status"
                value={createStatus}
                onChange={(e) => setCreateStatus(Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </ModalSelect>
            </ModalFormGroup>

            <ModalFormGroup>
              <ModalLabel htmlFor="create-privacy-policy-url">
                Privacy Policy URL (optional)
              </ModalLabel>
              <ModalInput
                id="create-privacy-policy-url"
                type="url"
                value={privacyPolicyUrl}
                onChange={(e) => {
                  setPrivacyPolicyUrl(e.target.value);
                  setCreateErrors((prev) => ({
                    ...prev,
                    privacy_policy_url: undefined,
                  }));
                }}
                placeholder="https://example.com/privacy"
                $hasError={!!createErrors.privacy_policy_url}
              />
              {createErrors.privacy_policy_url && (
                <FieldError>{createErrors.privacy_policy_url}</FieldError>
              )}
            </ModalFormGroup>

            <ModalFormGroup>
              <ModalLabel htmlFor="create-terms-url">
                Terms and Conditions URL (optional)
              </ModalLabel>
              <ModalInput
                id="create-terms-url"
                type="url"
                value={termsAndConditionsUrl}
                onChange={(e) => {
                  setTermsAndConditionsUrl(e.target.value);
                  setCreateErrors((prev) => ({
                    ...prev,
                    terms_and_conditions_url: undefined,
                  }));
                }}
                placeholder="https://example.com/terms"
                $hasError={!!createErrors.terms_and_conditions_url}
              />
              {createErrors.terms_and_conditions_url && (
                <FieldError>{createErrors.terms_and_conditions_url}</FieldError>
              )}
            </ModalFormGroup>

            <ModalButtonGroup>
              <ModalButton
                type="button"
                $variant="secondary"
                onClick={closeCreateModal}
                disabled={creating}
              >
                Cancel
              </ModalButton>
              <ModalButton
                type="button"
                onClick={handleCreatePartner}
                disabled={
                  creating ||
                  !createName.trim() ||
                  Object.keys(
                    getPartnerUrlErrors(
                      privacyPolicyUrl,
                      termsAndConditionsUrl,
                    ),
                  ).length > 0
                }
              >
                {creating ? "Creating..." : "Create"}
              </ModalButton>
            </ModalButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}

      {editingPartner && (
        <ModalOverlay onClick={closeEditModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>Edit Partner</h2>
              <CloseButton
                type="button"
                onClick={closeEditModal}
                aria-label="Close"
              >
                ×
              </CloseButton>
            </ModalHeader>

            <ModalFormGroup>
              <ModalLabel htmlFor="partner-name">Name</ModalLabel>
              <ModalInput
                id="partner-name"
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </ModalFormGroup>

            <ModalFormGroup>
              <ModalLabel htmlFor="partner-status">Status</ModalLabel>
              <ModalSelect
                id="partner-status"
                value={editStatus}
                onChange={(e) => setEditStatus(Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </ModalSelect>
            </ModalFormGroup>

            <ModalFormGroup>
              <ModalLabel htmlFor="partner-privacy-policy-url">
                Privacy Policy URL (optional)
              </ModalLabel>
              <ModalInput
                id="partner-privacy-policy-url"
                type="url"
                value={editPrivacyPolicyUrl}
                onChange={(e) => {
                  setEditPrivacyPolicyUrl(e.target.value);
                  setEditErrors((prev) => ({
                    ...prev,
                    privacy_policy_url: undefined,
                  }));
                }}
                placeholder="https://example.com/privacy"
                $hasError={!!editErrors.privacy_policy_url}
              />
              {editErrors.privacy_policy_url && (
                <FieldError>{editErrors.privacy_policy_url}</FieldError>
              )}
            </ModalFormGroup>

            <ModalFormGroup>
              <ModalLabel htmlFor="partner-terms-url">
                Terms and Conditions URL (optional)
              </ModalLabel>
              <ModalInput
                id="partner-terms-url"
                type="url"
                value={editTermsAndConditionsUrl}
                onChange={(e) => {
                  setEditTermsAndConditionsUrl(e.target.value);
                  setEditErrors((prev) => ({
                    ...prev,
                    terms_and_conditions_url: undefined,
                  }));
                }}
                placeholder="https://example.com/terms"
                $hasError={!!editErrors.terms_and_conditions_url}
              />
              {editErrors.terms_and_conditions_url && (
                <FieldError>{editErrors.terms_and_conditions_url}</FieldError>
              )}
            </ModalFormGroup>

            <ModalButtonGroup>
              <ModalButton
                type="button"
                $variant="secondary"
                onClick={closeEditModal}
                disabled={saving}
              >
                Cancel
              </ModalButton>
              <ModalButton
                type="button"
                onClick={handleSavePartner}
                disabled={
                  saving ||
                  !editName.trim() ||
                  Object.keys(
                    getPartnerUrlErrors(
                      editPrivacyPolicyUrl,
                      editTermsAndConditionsUrl,
                    ),
                  ).length > 0
                }
              >
                {saving ? "Saving..." : "Save"}
              </ModalButton>
            </ModalButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Partners;
